'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from 'react';
import { Settings2 } from 'lucide-react';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { GlassPopoverContent } from '@/shared/ui/glass';
import { cn } from '@/lib/utils';
import { useNotificationStore } from '../../model/notification-store';
import { closeNotificationCentre, useNotificationCentreStore } from '../../model/notification-centre-store';
import { hasRoomForRail, useCentreWidth } from '../../model/use-centre-width';
import { CarriedStrip } from './carried-strip';
import { CentreToolbarLeft, CentreToolbarRight } from './centre-toolbar';
import { DaySection } from './day-section';
import { OlderDays } from './older-days';
import { filterDay, keeps, type Lens } from './lenses';
import { PreferencesPanel } from './preferences-panel';
import { SettledFolds } from './settled-folds';
import { useNotifications } from '../../model/use-notifications';
import { useNotificationSearchResults } from '../../model/use-notification-search';

/** Which figure on the envelope each lens reads. */
const LENS_FIGURE: Readonly<Record<Lens, 'lensAll' | 'lensNeeds' | 'lensMine' | 'lensFirm'>> = {
  all: 'lensAll',
  needs: 'lensNeeds',
  mine: 'lensMine',
  firm: 'lensFirm'
};

/**
 * The centre: the days, with the digests as their headings.
 *
 * The page is the archive, and the archive is a calendar. Today is open
 * because it is not finished; every day behind it is one heading, one sentence
 * and a count, and opens to its rows. The carried strip sits above all of it
 * and never folds, so the oldest unfinished thing in the book is at the top of
 * the page rather than at the bottom of a scroll.
 *
 * The read line is drawn in exactly the place the bell drew it, inside
 * whichever day contains it, so arriving here from the bell is a widening
 * rather than a change of subject.
 *
 * THE CONTROLS ARE THIS SURFACE'S OWN HEADER ROW. The search, the four lenses,
 * the count, "Everything seen" and Close are built here, because this is where
 * the model they read is derived, and they render at the top of the cover. On
 * the book they were projected into the shell's navbar slots; the platform
 * navbar publishes no slots, and the cover here is the whole page rather than
 * one column of a chrome, so a header row of its own is not a second chrome row
 * under anything.
 *
 * The preferences are a rail when the cover has the room, because the digest
 * hour is the thing that decides what a day IS on this surface and it should be
 * one glance away from the days it shapes. Below 1000 the rail would cost the
 * days 300 of their width, so it becomes a button and a popover instead. The
 * width is the cover's OWN, measured by `useCentreWidth`, so a sidebar or a
 * zoom that changes it re-lays the centre without the viewport changing.
 */

export function NotificationCentre() {
  const $ = _c(67);
  const {
    ref: bodyRef,
    width
  } = useCentreWidth();
  let t0;
  if ($[0] !== width) {
    t0 = hasRoomForRail(width);
    $[0] = width;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const wide = t0;
  const search = useNotificationCentreStore(_temp);
  const setSearch = useNotificationCentreStore(_temp2);
  const [lens, setLens] = useState("all");
  const markAllRead = useNotificationStore(_temp3);
  const digest = useNotificationStore(_temp4);
  const tray = useNotifications();
  const searched = useNotificationSearchResults();
  const model = searched ?? tray;
  let t1;
  if ($[2] !== lens || $[3] !== model.carried) {
    t1 = model.carried.filter(keeps(lens));
    $[2] = lens;
    $[3] = model.carried;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const carried = t1;
  let t2;
  if ($[5] !== lens || $[6] !== model.days) {
    let t3;
    if ($[8] !== lens) {
      t3 = d => filterDay(d, lens);
      $[8] = lens;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    t2 = model.days.map(t3).filter(_temp5);
    $[5] = lens;
    $[6] = model.days;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const days = t2;
  const searching = searched !== null;
  const looking = searched?.searching === true;
  const searchFailed = searched?.failed === true;
  const heldBelow = (searched?.rows.length ?? 0) > 0;
  const live = searched ? searched.figures?.[LENS_FIGURE[lens]] ?? 0 : tray.lensCounts[lens];
  const close = _temp6;
  let t3;
  if ($[10] !== carried.length || $[11] !== days.length || $[12] !== lens || $[13] !== live || $[14] !== search || $[15] !== setSearch) {
    t3 = <CentreToolbarLeft search={search} onSearch={setSearch} lens={lens} onLens={setLens} carried={carried.length} live={live} days={days.length} />;
    $[10] = carried.length;
    $[11] = days.length;
    $[12] = lens;
    $[13] = live;
    $[14] = search;
    $[15] = setSearch;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const toolbarLeft = t3;
  let t4;
  if ($[17] !== markAllRead) {
    t4 = <CentreToolbarRight onMarkAllRead={markAllRead} onClose={close} />;
    $[17] = markAllRead;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const toolbarRight = t4;
  let t5;
  if ($[19] !== toolbarLeft || $[20] !== toolbarRight) {
    t5 = <div className="flex w-full min-w-0 shrink-0 flex-wrap items-center justify-between gap-2">{toolbarLeft}{toolbarRight}</div>;
    $[19] = toolbarLeft;
    $[20] = toolbarRight;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== wide) {
    t6 = wide ? null : <div className="flex shrink-0 justify-end"><Popover><PopoverTrigger asChild={true}><button type="button" className="flex h-7 items-center gap-1.5 rounded-full bg-white dark:bg-white/12 px-2.5 text-[11px] font-medium text-[#44546F] dark:text-white/70 shadow-[0_1px_3px_rgba(15,23,42,0.08)] transition-shadow hover:text-[#091E42] dark:hover:text-white hover:shadow-[0_2px_8px_rgba(86,81,255,0.15)] data-[state=open]:text-[#091E42] dark:data-[state=open]:text-white data-[state=open]:shadow-[0_2px_8px_rgba(86,81,255,0.15)]"><Settings2 className="h-3 w-3" />Preferences</button></PopoverTrigger><GlassPopoverContent align="end" className="max-h-[520px] w-[340px] overflow-y-auto p-3"><PreferencesPanel /></GlassPopoverContent></Popover></div>;
    $[22] = wide;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const t7 = wide ? "flex-row" : "flex-col";
  let t8;
  if ($[24] !== t7) {
    t8 = cn("-m-1 flex min-h-0 flex-1 gap-3 overflow-y-auto p-1", t7);
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== carried) {
    t9 = <CarriedStrip threads={carried} />;
    $[26] = carried;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== heldBelow || $[29] !== searchFailed) {
    t10 = searchFailed ? <p className="rounded-2xl bg-white px-4 py-4 text-center text-[12px] text-[#44546F] dark:bg-white/12 dark:text-white/70">{heldBelow ? "That search did not come back. What is below is the last answer it gave; type again to retry." : "That search did not come back. Type again to retry."}</p> : null;
    $[28] = heldBelow;
    $[29] = searchFailed;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== days || $[32] !== digest || $[33] !== looking || $[34] !== model.onFire || $[35] !== model.position || $[36] !== searchFailed || $[37] !== searching) {
    t11 = days.length === 0 && looking ? <p className="rounded-2xl bg-white px-4 py-6 text-center text-[12px] text-[#44546F] dark:bg-white/12 dark:text-white/70">Looking through the history.</p> : days.length === 0 && !searchFailed ? <p className="rounded-2xl bg-white px-4 py-6 text-center text-[12px] text-[#44546F] dark:bg-white/12 dark:text-white/70">Nothing here matches. The carried strip above ignores filters, because a thing that is blocked on you does not stop being blocked when you narrow the view.</p> : days.map((day, i) => <DaySection key={`${day.key}:${searching ? "open" : "folded"}`} day={day} digest={digest} markerAt={model.position} onFire={model.onFire} defaultOpen={i === 0 || !day.closed || searching} />);
    $[31] = days;
    $[32] = digest;
    $[33] = looking;
    $[34] = model.onFire;
    $[35] = model.position;
    $[36] = searchFailed;
    $[37] = searching;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== model.atCeiling || $[40] !== model.hasOlder || $[41] !== model.loadOlder || $[42] !== model.loaded || $[43] !== model.loadingOlder || $[44] !== model.total || $[45] !== model.truncated) {
    t12 = <OlderDays truncated={model.truncated} shown={model.loaded} total={model.total} hasOlder={model.hasOlder} atCeiling={model.atCeiling} loading={model.loadingOlder} onLoadOlder={model.loadOlder} />;
    $[39] = model.atCeiling;
    $[40] = model.hasOlder;
    $[41] = model.loadOlder;
    $[42] = model.loaded;
    $[43] = model.loadingOlder;
    $[44] = model.total;
    $[45] = model.truncated;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== model.done || $[48] !== model.snoozed) {
    t13 = <SettledFolds snoozed={model.snoozed} done={model.done} />;
    $[47] = model.done;
    $[48] = model.snoozed;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== t10 || $[51] !== t11 || $[52] !== t12 || $[53] !== t13 || $[54] !== t9) {
    t14 = <div className="min-w-0 flex-1 space-y-2">{t9}{t10}{t11}{t12}{t13}</div>;
    $[50] = t10;
    $[51] = t11;
    $[52] = t12;
    $[53] = t13;
    $[54] = t9;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  let t15;
  if ($[56] !== wide) {
    t15 = wide ? <aside className="w-[300px] shrink-0 self-start"><PreferencesPanel /></aside> : null;
    $[56] = wide;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== t14 || $[59] !== t15 || $[60] !== t8) {
    t16 = <div className={t8}>{t14}{t15}</div>;
    $[58] = t14;
    $[59] = t15;
    $[60] = t8;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  let t17;
  if ($[62] !== bodyRef || $[63] !== t16 || $[64] !== t5 || $[65] !== t6) {
    t17 = <div ref={bodyRef} className="flex h-full min-h-0 w-full flex-col gap-2 overflow-x-hidden p-3">{t5}{t6}{t16}</div>;
    $[62] = bodyRef;
    $[63] = t16;
    $[64] = t5;
    $[65] = t6;
    $[66] = t17;
  } else {
    t17 = $[66];
  }
  return t17;
}
function _temp6() {
  return closeNotificationCentre();
}
function _temp5(d_0) {
  return d_0.threads.length > 0;
}
function _temp4(s_2) {
  return s_2.preferences.digest;
}
function _temp3(s_1) {
  return s_1.markAllRead;
}
function _temp2(s_0) {
  return s_0.setSearch;
}
function _temp(s) {
  return s.search;
}